<template>
  <fieldset id="new-benefit-quote">
    <ApiLoading v-if="checkSomeObjectPropertiesTrue(apiStatus)" :apiError="apiStatus.fail" class="api-loading" isMemberPortal>
      <p>Sorry, something has gone wrong, and we can’t show you all the information.</p>
      <p>Try refreshing your page.</p>
    </ApiLoading>

    <div class="form-section" v-else>
      <div class="form-header">
        <h2 class="h4">{{ isClaim ? `Member and claim type` : `New benefit quote` }}</h2>
        <p>* indicates required field</p>
      </div>
      <div v-if="showNoPaymentInfoAlert">
        <FormField>
          <Alert
            class="no-margin-bottom"
            :message="
              !this.isCRM
                ? ErrorStrings.GENERIC_ERROR
                : `This member has no claims account on file. Please nominate the policy holder's stored account. Otherwise, please discard the claim, and create a work item for a Member Services consultant to contact the member and update their claiming account details.`
            "
          >
            <div v-if="!isCRM">
              <div v-if="!hasClaimsAccount" v-html="refData.content.claimsAccountAlertText.informationText.tokenSwap()" />
              <div v-else v-html="refData.content.claimsAccountAlertTextAlt.informationText" />
            </div>
          </Alert>
        </FormField>
      </div>

      <div class="form-section" v-if="memberList().length > 0">
        <FormField
          labelText="Date of service"
          placeholder="DD / MM / YYYY"
          v-maska="'## / ## / ####'"
          id="sevicedate-select"
          type="text"
          isDateInput
          maxlength="14"
          v-model.lazy.trim="v$.formResponse.serviceDate.$model"
          :error="v$.formResponse.serviceDate.$error"
          :errorMessage="dateErrorMessage(v$.formResponse.serviceDate)"
        />
      </div>

      <div class="form-section" v-if="memberList().length > 0">
        <FormField labelText="Member receiving services">
          <DropdownSelect :items="formattedMemberList()" v-model="v$.formResponse.member.$model" :initialSelect="dropDownInitialMember" />
          <template v-if="!isCRM && refData.content.memberReceivingServicesHelpText" v-slot:helperText>
            <div v-html="refData.content.memberReceivingServicesHelpText.informationText" />
          </template>
        </FormField>
      </div>

      <div class="form-section" v-if="modalityList.length > 0 && formResponse.serviceDate && !v$.formResponse.serviceDate.$error">
        <FormField labelText="Select claim type" forId="modality-select">
          <MultiSelect
            :key="`modality ${formResponse.modality} ${formResponse.member}`"
            id="modality-select"
            :maximum="1"
            :items="modalityList"
            :selectedItems="selectedModality"
            placeholderText="Select claim type"
            @updateData="updateModality"
            autocomplete="off"
          />
        </FormField>
      </div>
    </div>
    <div class="form-page-nav">
      <section>
        <button @click="handleNextPage(currentPage + 1)" :disabled="v$.$invalid || v$.$error">Next</button>
      </section>
    </div>
  </fieldset>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { ErrorStrings } from '@general/js/constants';
import { checkSomeObjectPropertiesTrue } from '@general/js/utils/ObjectUtils';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { AccessLevel } from '@/enums';
import { Alert } from '@/components';
import { isCurrentOrFutureDate, isDateInRange, sortMembersByRelationshipDisplay } from '@/utils';
import { DATE_VALIDATOR } from '@general/js/validators';
import { DropdownSelect } from '@general/js/components';
import GABQnMANCStepsMixins from './GABQnMANC_StepsMixins';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'GABQ-step-1',

  mixins: [GABQnMANCStepsMixins],

  components: {
    Alert,
    DropdownSelect,
  },

  data() {
    return {
      modalityList: [],
      formResponse: {
        serviceDate: '',
        member: '',
        modality: '',
      },
      ErrorStrings,
    };
  },

  computed: {
    ...mapGetters({
      members: VUEX_GENERAL.GETTERS.getMembers,
    }),

    showNoPaymentInfoAlert() {
      if (!this.isClaim) {
        return false;
      }

      // Check if selected member has payment information
      if (this.refData.accounts) {
        return this.selectedMemberAccountDetails === null && this.refData.accounts.length > 0;
      }
      return true;
    },

    selectedModality() {
      /**
       * MultiSelect SFC always need to have arr as selectedItems prop
       * find currently option object of currently selected modality
       */

      const currentModality = this.modalityList.find((modality) => {
        return modality.returnValue === this.formResponse.modality;
      });
      const returnArr = [];

      if (currentModality !== undefined) {
        returnArr.push(currentModality);
      }

      return returnArr;
    },

    hasClaimsAccount() {
      /**
       * Checks if claim account exists for currentMember
       */
      const accounts = this.refData.accounts;
      const selectedMember = this.formResponse.member; // person code
      const currentMember = this.currentMemberDetail.personCode; // person code

      // Returns person code with a claims account
      let claimsAccounts = [];
      if (accounts) {
        claimsAccounts = accounts
          .filter((account) => {
            return account.claimsAccount !== null;
          })
          .map((x) => {
            return x.personCode;
          });
      }

      // Checks if current member person code exists in claims account list
      return claimsAccounts.includes(selectedMember) || selectedMember !== currentMember;
    },

    hasAccessLevel() {
      /**
       * Check if the membership has either an account or access level
       */
      const members = this.refData.members;
      const accounts = this.refData.accounts;
      const selectedMember = this.formResponse.member; // person code
      const currentMember = this.currentMemberDetail.personCode; // person code

      if (accounts && members && selectedMember && currentMember) {
        // Map members and accout details into the same object
        const memberAccountDetails = accounts.map((member) => {
          const currentAccount = members.find((account) => {
            return account.personCode === member.personCode;
          });
          if (currentAccount) {
            return { ...member, ...currentAccount };
          }
          return member;
        });

        // Grabs policy holder object
        // For child only policy, in Fetch Products and Modalities response payload, it doesn't return policy holder details (as a not covered person) in members array, so use the full members list that retrieved from getMembers api
        const policyHolderPersonCode = this.members.find((member) => {
          return member?.accessLevel?.toLowerCase() === AccessLevel.POLICY_HOLDER_ACCESS;
        })?.personCode;

        const policyHolder = memberAccountDetails.find((member) => {
          return member.personCode === policyHolderPersonCode;
        });

        // Grabs current member object
        const currentMemberObject = this.members.find((member) => {
          return member.personCode === currentMember;
        });

        const validMember = memberAccountDetails.map((member) => {
          const basemodel = { personCode: member.personCode, valid: false };
          // Policy holder and full access members
          if (currentMemberObject?.accessLevel?.toLowerCase() !== AccessLevel.SELF_ACCESS) {
            // Check if current member or policy holder has a claims account
            if (member?.claimsAccount != null || policyHolder?.claimsAccount != null) {
              basemodel.valid = true;
            }
            // Self access members: Checks if current member has a claims account
          } else if (member?.claimsAccount != null && selectedMember === currentMember) {
            basemodel.valid = true;
          }
          return basemodel;
        });

        // Checks if the selected member is valid
        const isSelectedValid = validMember.find((member) => {
          return member.personCode === selectedMember;
        });
        if (isSelectedValid) {
          return isSelectedValid.valid;
        }
      }
      return false;
    },

    dropDownInitialMember() {
      return (
        this.formattedMemberList().find((member) => {
          return member.value === this.formResponse.member;
        })?.name ?? 'Please select one'
      );
    },
  },

  validations() {
    const today = new Date(new Date().toDateString());
    const twoYearsAgoToday = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate());

    return {
      formResponse: {
        serviceDate: {
          required,
          DATE_VALIDATOR,
          isCurrentOrFutureDate: (value) => {
            if (this.isClaim) return true;
            return isCurrentOrFutureDate(value);
          },
          dateInRange: (date) => {
            if (!this.isClaim) return true;
            return isDateInRange(date, twoYearsAgoToday, today);
          },
        },
        member: {
          required,
        },
        modality: {
          required,
        },
      },
      refData: {
        accounts: {
          checkAccessLevel() {
            if (!this.isClaim) return true;
            return this.hasAccessLevel;
          },
        },
      },
    };
  },

  beforeMount() {
    this.formResponse = { ...this.formResponse, ...this.feature.step1 };

    if (!this.formResponse.serviceDate || typeof this.formResponse.serviceDate !== 'string') {
      this.formResponse.serviceDate = '';
    }

    if (this.formResponse.member === '') {
      // by default select current member
      this.formResponse.member = this.currentMemberDetail.personCode;
    }

    this.refreshModalityList();
    this.updateFormResponse({ ...cloneDeep(this.feature), step1: this.formResponse });
  },

  mounted() {
    /**
     * dynamically add watcher for member from formResponse
     * this is to avoid unwanted update modality to empty string in before mount where it updates member data
     */
    this.$watch('formResponse.member', (newMember) => {
      this.resetFormResponses();
      this.formResponse.member = newMember;
      this.refreshModalityList();
    });

    /**
     * dynamically add watcher for modality from formResponse
     * when selected modality changes, reset future steps as per requirement
     */
    this.$watch('formResponse.modality', () => {
      this.resetFormResponses();
    });

    this.$watch('formResponse.serviceDate', () => {
      this.resetFormResponses();
      this.refreshModalityList();
    });
  },

  methods: {
    checkSomeObjectPropertiesTrue,

    updateModality(data) {
      const newFormResponse = { ...this.formResponse };
      newFormResponse.modality = data[0]?.returnValue ?? '';
      newFormResponse.productCode = data[0]?.productCode ?? '';
      this.formResponse = newFormResponse;
    },

    refreshModalityList() {
      this.modalityList = this.modalities(this.formResponse.member, this.formResponse.serviceDate);
    },

    dateErrorMessage(v) {
      if (v.DATE_VALIDATOR?.$invalid) {
        return "We can't accept that input - please remove letters and symbols";
      }

      if (!this.isClaim && v.isCurrentOrFutureDate?.$invalid) {
        return "Please enter today's date or a future date.";
      }

      if (this.isClaim && v.dateInRange?.$invalid) {
        return "The date of service must be a past date within two years of today's date.";
      }

      return '';
    },

    memberList() {
      const policyNumber = this.isCRM ? this.CRM_POLICY_NUMBER : this.$route.params.policyNumber;
      const currentAccessLevel = this.accessLevel(policyNumber);
      const refDataMembers = this.refData.members;

      // if self access, member list only contains this member
      if (currentAccessLevel === AccessLevel.SELF_ACCESS) {
        return refDataMembers.filter((member) => {
          return member.personCode === this.currentMemberDetail.personCode;
        });
      }

      if (!this.isCRM && this.members) {
        const personCodes = refDataMembers.map((member) => {
          return member.personCode;
        });

        const sortedMemberList = sortMembersByRelationshipDisplay(
          this.members.filter((member) => {
            return personCodes.includes(member.personCode);
          }),
        );

        return sortedMemberList;
      }

      return refDataMembers;
    },

    formattedMemberList() {
      const members = this.memberList();

      const formattedMemberList = members.map((member) => {
        return { name: `${member.firstName} ${member.lastName}`, value: member.personCode };
      });

      return formattedMemberList;
    },
  },
};
</script>

<style lang="scss" scoped>
.no-margin-bottom {
  margin-bottom: 0 !important;
}

.form-header {
  margin-bottom: 48px;

  h2 {
    margin-bottom: 0.25rem;
  }

  p {
    margin-bottom: 0;
  }
}
</style>
